/**
 * Club branding → CSS custom properties.
 *
 * Writes ONLY the club-overridable tokens onto :root (see theme.css). Neutrals, spacing and
 * semantic colours are fixed so a club colour choice can never break the design.
 * Pattern carried over from prior art (RegalPortal BrandingContext.applyCSSVariables).
 */

export interface ClubBranding {
  clubName: string;
  shortName: string;
  primaryColour: string;
  secondaryColour: string;
  /** Text/icon colour used on brand-primary surfaces. */
  contrastColour?: string;
  logoUrl?: string;
}

const CACHE_KEY = 'pb_branding';
const CACHE_TTL_MS = 5 * 60 * 1000;

export function applyClubTheme(branding: ClubBranding): void {
  const root = document.documentElement;
  root.style.setProperty('--brand-primary', branding.primaryColour);
  root.style.setProperty('--brand-secondary', branding.secondaryColour);
  if (branding.contrastColour) {
    root.style.setProperty('--brand-contrast', branding.contrastColour);
  }
  document.title = branding.clubName;
  // Keep the browser/PWA chrome colour in step with the manifest's theme_color.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', branding.primaryColour);
}

/** Remove runtime overrides, falling back to theme.css defaults (used by the live-preview revert). */
export function resetClubTheme(): void {
  const root = document.documentElement;
  for (const v of ['--brand-primary', '--brand-secondary', '--brand-contrast']) {
    root.style.removeProperty(v);
  }
}

export function readCachedBranding(): ClubBranding | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { at, branding } = JSON.parse(raw) as { at: number; branding: ClubBranding };
    return Date.now() - at < CACHE_TTL_MS ? branding : null;
  } catch {
    return null;
  }
}

export function cacheBranding(branding: ClubBranding): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), branding }));
  } catch {
    /* storage unavailable (private mode) — cache is an optimisation only */
  }
}
